import { Numbering, NumberingStyle } from './number-utils.type';

export const DPI = 96;
export const INCHES_TO_CM = 2.54;
export const DEFAULT_SIZE_THRESHOLD = 10;
export type Unit = 'cm' | 'mm' | 'in' | 'px' | 'pt';
export type Dimension = {
  width: number;
  height: number;
  unit: Unit;
};

export function convertToNumber(value: string | number | undefined | null): number | undefined {
  const num = Number(value);

  return Number.isNaN(num) ? undefined : num;
}

/**
 * Round to the nearest even integer
 */
export function roundToEven(value: number): number {
  return 2 * Math.round(value / 2);
}

export function round(value: number, decimalPlaces = 2): number {
  const factor = Math.pow(10, decimalPlaces);

  return Math.round(value * factor) / factor;
}

export function convertToPixel(value: number, unit: Unit, dpi?: number, roundEven = false): number {
  const roundFunction = roundEven ? roundToEven : round;

  switch (unit) {
    case 'cm':
      return roundFunction((value * (dpi ?? DPI)) / INCHES_TO_CM);
    case 'mm':
      return roundFunction((value * (dpi ?? DPI)) / (INCHES_TO_CM * 10));
    case 'in':
      return roundFunction(value * (dpi ?? DPI));
    default:
      return roundFunction(value);
  }
}

export function getSizeFromSvg(document: any): {
  width: number;
  height: number;
} {
  const svgElement = document.querySelector('svg');

  const width = Number(svgElement.getAttribute('width'));
  const height = Number(svgElement.getAttribute('height'));

  return { width, height };
}

export function transformNumberToStyle(value: number, style: NumberingStyle): Numbering {
  switch (style) {
    case 'numbers':
      return Number(value).toString();
    case 'numbers-with-two-digits':
      return numberWithMinDigits(value, 2).toString();
    case 'numbers-with-three-digits':
      return numberWithMinDigits(value, 3).toString();
    case 'numbers-with-four-digits':
      return numberWithMinDigits(value, 4).toString();
    case 'letters':
      return numberToChar(value, false);
    case 'capital-letters':
      return numberToChar(value, true);
    case 'roman-numbers':
      return numberToRoman(value);
    case 'roman-numbers-lower':
      return numberToRoman(value).toLowerCase();
    default:
      return String(value);
  }
}

export function numberWithMinDigits(value: number, minDigits: number): string {
  return new Intl.NumberFormat('en', {
    minimumIntegerDigits: minDigits,
    maximumFractionDigits: 0,
    useGrouping: false,
  }).format(value);
}

export function numberToRoman(value: number): string {
  const digits = String(+value).split('');
  const key = [
    '',
    'C',
    'CC',
    'CCC',
    'CD',
    'D',
    'DC',
    'DCC',
    'DCCC',
    'CM',
    '',
    'X',
    'XX',
    'XXX',
    'XL',
    'L',
    'LX',
    'LXX',
    'LXXX',
    'XC',
    '',
    'I',
    'II',
    'III',
    'IV',
    'V',
    'VI',
    'VII',
    'VIII',
    'IX',
  ];
  let roman = '';
  let i = 3;

  while (i--) {
    roman = (key[+(digits.pop() ?? 0) + i * 10] || '') + roman;
  }

  return Array(+digits.join('') + 1).join('M') + roman;
}

export function numberToChar(value: number, uppercase = false, startingAt = 1): string {
  let baseNumber = value - startingAt;
  const reference = 'abcdefghijklmnopqrstuvwxyz';
  const alphabet = uppercase ? reference.toUpperCase() : reference;
  const base = alphabet.length;
  const digits: number[] = [];
  const chars = [];

  do {
    const digit = baseNumber % base;

    digits.push(digit);
    baseNumber = Math.floor(baseNumber / base);
  } while (baseNumber-- > 0);

  while (digits.length) {
    chars.push(alphabet[digits.pop() ?? 0]);
  }

  return chars.join('');
}

export function isMatchedSize(
  base: Dimension,
  target: Dimension,
  threshold = DEFAULT_SIZE_THRESHOLD
): boolean {
  const baseInPixel = {
    width: convertToPixel(base.width, base.unit),
    height: convertToPixel(base.height, base.unit),
  };

  const targetInPixel = {
    width: convertToPixel(target.width, target.unit),
    height: convertToPixel(target.height, target.unit),
  };

  return (
    Math.abs(baseInPixel.width - targetInPixel.width) < threshold &&
    Math.abs(baseInPixel.height - targetInPixel.height) < threshold
  );
}
